import { inject, resource, Service } from '@angular/core';
import { DateTime } from 'luxon';
import { CookieManager } from '../storage/cookie-manager';
import { MatomoClient } from './matomo/matomo-client';

export const COOKIE_CONSENT_ASKED_KEY = 'cookie-consent-asked';
export const YOUTUBE_CONSENT_GIVEN_KEY = 'youtube-consent-given';

@Service()
export class CookieConsentManager {
  private readonly matomoClient = inject(MatomoClient);
  private readonly cookieManager = inject(CookieManager);

  readonly consentAsked = this.cookieManager.get(COOKIE_CONSENT_ASKED_KEY);

  private readonly youtubeConsent = this.cookieManager.observe(YOUTUBE_CONSENT_GIVEN_KEY);

  readonly isYoutubeConsentGiven = resource<boolean, boolean>({
    params: ({ chain }) => chain(this.youtubeConsent)?.value === 'true',
    loader: async ({ params }) => params,
    defaultValue: false,
  }).asReadonly();
  readonly isMatomoConsentGiven = this.matomoClient.isConsentGiven;

  acceptMatomoConsent(): void {
    this.matomoClient.rememberConsent();
    this.storeConsentAsked(true);
  }

  rejectMatomoConsent(): void {
    this.matomoClient.forgetConsent();
    this.storeConsentAsked(true);
  }

  acceptYoutubeConsent(): void {
    this.storeYoutubeConsent(true);
    this.storeConsentAsked(true);
  }

  rejectYoutubeConsent(): void {
    this.storeYoutubeConsent(false);
    this.storeConsentAsked(true);
  }

  acceptAll(): void {
    this.acceptMatomoConsent();
    this.acceptYoutubeConsent();
  }

  rejectAll(): void {
    this.rejectMatomoConsent();
    this.rejectYoutubeConsent();
  }

  private storeYoutubeConsent(value: boolean): void {
    this.cookieManager.set({
      name: YOUTUBE_CONSENT_GIVEN_KEY,
      value: value.toString(),
      expires: DateTime.now().plus({ months: 6 }).toMillis(),
    });
  }

  private storeConsentAsked(value: boolean): void {
    this.cookieManager.set({
      name: COOKIE_CONSENT_ASKED_KEY,
      value: value.toString(),
      expires: DateTime.now().plus({ months: 6 }).toMillis(),
    });
  }
}
